import moment from '@/plugins/vue-moment.js';

/**
 * Returns true when both dates are present and the end date is before the start date.
 */
export function isInvalidReportDateRange(startDate, endDate, momentFn = moment) {
    if (!startDate || !endDate) {
        return false;
    }

    const start = momentFn(startDate);
    const end = momentFn(endDate);

    if (!start.isValid() || !end.isValid()) {
        return false;
    }

    return end.isBefore(start, 'day');
}

export async function readBlobAsText(blob) {
    if (typeof blob?.text === 'function') {
        return blob.text();
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error ?? new Error('Unable to read download error payload.'));
        reader.readAsText(blob);
    });
}

/**
 * Re-hydrate JSON error payloads that Axios returned as Blobs because the
 * request used responseType: 'blob' (typical for report CSV downloads).
 */
export async function hydrateAxiosBlobError(error) {
    const data = error?.response?.data;

    if (!(data instanceof Blob)) {
        return error;
    }

    const text = await readBlobAsText(data);

    try {
        error.response.data = JSON.parse(text);
    } catch {
        error.response.data = { message: text || 'Unable to download the report.' };
    }

    return error;
}

/**
 * Prefer the server's explicit message (including Laravel validation errors),
 * then fall back to a clear client-side message.
 */
export function resolveReportDownloadErrorMessage(error, translate = key => key) {
    const responseData = error?.response?.data;

    if (responseData?.message) {
        return responseData.message;
    }

    const endDateError = responseData?.errors?.end_date?.[0];
    if (endDateError) {
        return endDateError;
    }

    const startDateError = responseData?.errors?.start_date?.[0];
    if (startDateError) {
        return startDateError;
    }

    if (!error?.response) {
        return translate('report_download_network_error');
    }

    if (error?.message && !error.message.includes('status code')) {
        return error.message;
    }

    return translate('report_download_failed');
}

export function normalizeReportChartError(error, translate = key => key) {
    if (!error) {
        return null;
    }

    if (error.exceptionMessage) {
        return error;
    }

    return Object.assign(error, {
        exceptionMessage: resolveReportDownloadErrorMessage(error, translate),
    });
}
